/**
 * admin/makua/ops.js — small things every Makua operations screen shares.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export const money = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: Number.isInteger(v) ? 0 : 2, maximumFractionDigits: 2 });
};

/** "2026-11-06" read as a local date (never shifted by a timezone). */
export const asDate = (s) => {
  const m = String(s || "").match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0)) : null;
};
export const fmtDate = (s, opts = { day: "numeric", month: "short", year: "numeric" }) => {
  const d = asDate(s);
  return d ? d.toLocaleDateString("en-US", opts) : "—";
};
export const fmtStamp = (s) => {
  const d = asDate(s);
  return d ? d.toLocaleString("en-US", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "—";
};
export const range = (a, b) => `${fmtDate(a, { day: "numeric", month: "short" })} – ${fmtDate(b)}`;
export const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const addDays = (s, n) => { const d = asDate(s); d.setDate(d.getDate() + n); return ymd(d); };
export const today = () => ymd(new Date());

export const RES_STATUS = {
  pending_payment: { label: "Held · unpaid", tone: "amber" },
  confirmed: { label: "Confirmed", tone: "green" },
  requested: { label: "Request", tone: "purple" },
  cancelled: { label: "Cancelled", tone: "" },
  expired: { label: "Hold expired", tone: "red" },
};
export const SCREENING = {
  not_started: { label: "Not started", tone: "" },
  in_progress: { label: "In progress", tone: "amber" },
  approved: { label: "Cleared", tone: "green" },
  declined: { label: "Not cleared", tone: "red" },
  not_required: { label: "Not required", tone: "blue" },
};

export const Badge = ({ tone = "", children }) => <span className={`ops-badge ${tone ? `ops-badge--${tone}` : ""}`}>{children}</span>;
export const StatusBadge = ({ status }) => <Badge tone={RES_STATUS[status]?.tone}>{RES_STATUS[status]?.label || status}</Badge>;
export const ScreeningBadge = ({ status }) => <Badge tone={SCREENING[status]?.tone}>{SCREENING[status]?.label || status}</Badge>;

/** Calls an admin API (which answers { success, data, message }) and keeps the data. */
export function useLoad(fn, deps = []) {
  const [state, setState] = useState({ data: null, loading: true, error: "" });
  const alive = useRef(true);
  const run = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    return Promise.resolve().then(fn)
      .then((res) => { if (alive.current) setState({ data: res?.data ?? res, loading: false, error: "", raw: res }); })
      .catch((err) => { if (alive.current) setState((s) => ({ ...s, loading: false, error: err.message || "Couldn't load that." })); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { alive.current = true; run(); return () => { alive.current = false; }; }, [run]);
  return { ...state, reload: run };
}

/** A field with its label (and optional hint underneath). */
export const Field = ({ label, hint, children, className = "" }) => (
  <label className={`ops-field ${className}`}>
    <span>{label}</span>
    {children}
    {hint && <small>{hint}</small>}
  </label>
);

export const Modal = ({ title, onClose, wide, children }) => (
  <div className="ops-modal-scrim" onClick={onClose}>
    <div className={`ops-modal ${wide ? "ops-modal--wide" : ""}`} role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
      <div className="ops-card-head"><h3>{title}</h3><button type="button" className="ops-close" aria-label="Close" onClick={onClose}>×</button></div>
      {children}
    </div>
  </div>
);

/** Colour per retreat type, stable across screens: the same type is always the same colour. */
const PALETTE = ["#b7cf9c", "#a9c4e6", "#d8c2ec", "#f1d49b", "#9fd6cf", "#e8b4a0", "#c9d38b", "#b8b2e8", "#e6c3d6", "#a7d1a7"];
export const typeColour = (typeId) => (typeId ? PALETTE[Number(typeId) % PALETTE.length] : "#d9d4c8");
